import { useState } from 'react';
import { useEditorState } from '@/lib/kinema';
import { useEditorActions } from './editorActionsContext';
import { ProjectSettingsModal } from './ProjectSettingsModal';
import { hasNotes, useAdTokenFindings } from '../adTokenFindings';
import { AD_TOKEN_CHECKS_UNAVAILABLE } from '@/shared/adTokens/AdTokenMessages';

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.userAgent);
const cmdKey = isMac ? 'Cmd' : 'Ctrl';

function KeybindRow({ keys, label }: { keys: string; label: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span
        style={{
          fontFamily: 'monospace',
          fontSize: 11,
          color: '#475569',
          background: '#f1f5f9',
          padding: '1px 5px',
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        {keys}
      </span>
    </div>
  );
}

/**
 * The composition's ad token count beside its name, which shows and hides the drawer that
 * lists them (MPN-1933). With no vocabulary to check against, one neutral note instead.
 */
function AdTokenHeaderNote() {
  const { byLayer, warningCount, unavailable, drawerOpen, setDrawerOpen } = useAdTokenFindings();
  if (unavailable) {
    return <span style={{ fontWeight: 400, color: '#64748b' }}> · {AD_TOKEN_CHECKS_UNAVAILABLE}</span>;
  }
  if (!hasNotes(byLayer)) return null;
  const label = warningCount > 0
    ? `${warningCount} ad token warning${warningCount === 1 ? '' : 's'}`
    : 'Ad token notes';
  return (
    <>
      {' · '}
      <button
        type="button"
        onClick={() => setDrawerOpen((open) => !open)}
        aria-expanded={drawerOpen}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
          fontWeight: 400, color: warningCount > 0 ? '#dc2626' : '#64748b', textDecoration: 'underline',
        }}
      >
        {label}
      </button>
    </>
  );
}

interface HeaderBarProps {
  onBack?: () => void;
  projectName?: string;
}

export function HeaderBar({ onBack, projectName }: HeaderBarProps) {
  const state = useEditorState();
  const rootName = projectName ?? state.layers[state.rootLayerId]?.name ?? 'Untitled Project';
  const [showSettings, setShowSettings] = useState(false);
  const [showKeybinds, setShowKeybinds] = useState(false);
  const { saving, lastSaved, save, rendering, render, renderError } = useEditorActions();

  return (
    <>
      <div
        style={{
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {onBack && (
          <button
            onClick={onBack}
            title="Back"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748b',
              padding: 4,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              marginRight: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        <span style={{ color: '#1e293b', fontSize: 13, fontWeight: 600, flex: 1 }}>
          {rootName}
          <AdTokenHeaderNote />
        </span>
        <span style={{ fontSize: 10, color: renderError ? '#ef4444' : '#94a3b8', marginRight: 8 }}>
          {renderError
            ? renderError
            : rendering
              ? 'Rendering...'
              : saving
                ? 'Saving...'
                : lastSaved
                  ? `Saved ${lastSaved.toLocaleTimeString()}`
                  : ''}
        </span>
        <button
          onClick={save}
          disabled={saving}
          title="Save (Cmd+S)"
          style={{
            background: 'none',
            border: 'none',
            cursor: saving ? 'default' : 'pointer',
            color: saving ? '#cbd5e1' : '#64748b',
            padding: 4,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            marginRight: 4,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        </button>
        <button
          onClick={render}
          disabled={rendering || saving}
          title="Render"
          style={{
            background: 'none',
            border: 'none',
            cursor: rendering || saving ? 'default' : 'pointer',
            color: rendering ? '#845adf' : '#64748b',
            padding: 4,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            marginRight: 4,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
        </button>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowKeybinds(!showKeybinds)}
            title="Keyboard Shortcuts"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: showKeybinds ? '#2563eb' : '#64748b',
              padding: 4,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              marginRight: 4,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
          {showKeybinds && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 4,
                background: '#ffffff',
                color: '#475569',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                padding: 16,
                width: 260,
                zIndex: 200,
                boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                fontSize: 12,
                lineHeight: 1.8,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#1e293b' }}>Keyboard Shortcuts</div>
              <KeybindRow keys="V" label="Select tool" />
              <KeybindRow keys="H" label="Pan tool" />
              <KeybindRow keys="P" label="Pen tool" />
              <KeybindRow keys="Space" label="Play / Pause" />
              <KeybindRow keys={`${cmdKey}+S`} label="Save" />
              <KeybindRow keys={`${cmdKey}+Z`} label="Undo" />
              <KeybindRow keys={`${cmdKey}+Shift+Z`} label="Redo" />
              <KeybindRow keys={`${cmdKey}+C`} label="Copy layers" />
              <KeybindRow keys={`${cmdKey}+V`} label="Paste layers" />
              <KeybindRow keys={`${cmdKey}+D`} label="Duplicate layers" />
              <KeybindRow keys={`${cmdKey}+A`} label="Select all" />
              <KeybindRow keys="Arrow keys" label="Nudge 1px" />
              <KeybindRow keys="Shift+Arrow" label="Nudge 10px" />
              <KeybindRow keys="Delete" label="Delete layer" />
              <KeybindRow keys="Escape" label="Deselect / Cancel" />
              <KeybindRow keys={`${cmdKey}+Drag`} label="Disable snapping" />
              <KeybindRow keys={`${isMac ? 'Option' : 'Alt'}+Scale`} label="Fine scale" />
              <KeybindRow keys="Double-click path" label="Edit path points" />
            </div>
          )}
        </div>
        <button
          onClick={() => setShowSettings(true)}
          title="Project Settings"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#64748b',
            padding: 4,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </div>
      {showSettings && <ProjectSettingsModal onClose={() => setShowSettings(false)} />}
    </>
  );
}
